import clx from 'classnames';
import { useActions, useEspot, useBreakpoint } from '@app/hooks';
import { PageLoader } from 'cat-ecommerce-alloy';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as checkoutActionsList from '@app/store/checkout/actions';
import * as ordersActionsList from '@app/store/orders/actions';
import ItemsInOrder from '../checkout/ItemsInOrder';
import { containerPropTypes } from './declarations.propTypes';
import SummaryAndPaymentPage from './SummaryAndPaymentPage';
import SummaryAndPaymentPageHeader from './SummaryAndPaymentPageHeader';
import SummaryOrderTotals from './SummaryOrderTotals';
import { useGetShoppingPreferenceDataLoadStatus } from '@app/hooks/useGetShoppingPreferenceDataLoadStatus';
import styles from './styles.module.scss';
import CheckoutStepper from '../checkout/CheckoutStepper/CheckoutStepper';
import {
  RBD_UPDATE_FROM_SUMMARY_AND_PAYMENT,
  STATUS,
  PAGE_BREAKPOINTS,
  ORDER_STATUS,
  USER_ROLES
} from '@app/constants/commonConstants';
import Conditional from '@app/components/common/Conditional/Conditional';
import '@app/special-characters-support.scss';
import { useEffect, useState } from 'react';
import { getLocalStorage, isEmpty } from '@app/utils';
import {
  PCC_PS_PLACING_ORDER_UPDATES,
  PCC_PS_TITLE_UPDATES,
  PCC_PS_HIDE_ESPOTS_MOBILE,
  PCC_REFRESH_PARTS_AVAILABILITY_ON_QUOTE
} from '@app/constants/featureFlags';
import { useLocation } from 'react-router-dom';
import { PICKUP_AND_DELIVERY_PAGE } from '../../../constants/routes';
import LoaderWithText from '@app/components/common/LoaderWithText';
import { WIDGET_LOADER_SIZE } from './constants';
import { ORDER_TYPE_QOT } from '../account/common/OrdersToApproveTable/constants';

const Container = ({
  billingAddress = {},
  billingMethodOptions = [],
  additionalBillingMethodOptions = [],
  currencyNotation,
  includeGST,
  isSAndPLoadSuccessful,
  isSAndPLoading,
  isPendingQuote,
  savedCreditCard,
  requestByDate,
  micoEnabled,
  orderId,
  orderTotals,
  paymentGateway = {},
  paymentTokenizationEnabled,
  punchoutOrderFailed,
  selectedBillingAddress,
  selectedBillingMethod,
  selectedAlternativePayment,
  selectedAdditionalBillingMethod,
  promoCode,
  showGST,
  skipCreditCard,
  vatTaxEnabled,
  zeroFreightMessageEnabled,
  quoteEnabled,
  quoteAvailable,
  isPendingOrder,
  hideQuoteDetails
}) => {
  const [t] = useTranslation();
  const location = useLocation();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);

  const enableShoppingPreferencesCall = !isSAndPLoading;
  const [isDealerAcceptTermsAndCond, setDealerAcceptTermsAndCond] =
    useState(false);

  const { isPreferencesDataLoading, isPreferencesDataLoadCompleted } =
    useGetShoppingPreferenceDataLoadStatus(enableShoppingPreferencesCall);
  const checkoutActions = useActions(checkoutActionsList);
  const ordersActions = useActions(ordersActionsList);

  const orderInformation =
    useSelector(state => state.orders?.byId?.[orderId]) || {};
  const cartURL = useSelector(s => s.orders?.cartURL);

  const orderCallStatus = useSelector(
    s => s?.orders?.byId[orderId]?.orderCallStatus
  );
  const refreshPartsAvailabilityOnQuote = useSelector(
    s => s.featureFlag?.[PCC_REFRESH_PARTS_AVAILABILITY_ON_QUOTE]
  );
  const isCatCreditInternational = useSelector(
    state => state.dealer?.isCreditsInternationalPhaseOneEnabled
  );

  const remainingCatCreditsGlobal = useSelector(
    s =>
      s.checkout?.applyCatCreditsGlobal?.applyCatCreditsGlobalBalance
        ?.remainingCatCreditsGlobal
  );

  const catCreditsGlobalAmountUsed = useSelector(
    s =>
      s.checkout?.applyCatCreditsGlobal?.applyCatCreditsGlobalBalance
        ?.amountUsedCatCreditsGlobal
  );

  const pendingPageLoading = orderCallStatus === STATUS.PENDING;

  const {
    orderExtendAttribute = {},
    fullfillment: fulfillment, //Typo coming from backend
    purchaseOrderInfo: purchaseOrder,
    ascendingOrderItems,
    orderItemPrices
  } = orderInformation;

  const getupdatedName = (name, partNumber) => {
    if (name?.startsWith(partNumber + ':')) {
      return name?.replace(partNumber + ':', '');
    } else if (name?.startsWith(partNumber + ' ')) {
      return name?.replace(partNumber + ' ', '');
    } else {
      return name;
    }
  };

  const orderedItems = ascendingOrderItems?.map(
    ({ name, partNumber, ...item }) => ({
      ...item,
      partNumber,
      name: partNumber
        ? `${partNumber}: ${getupdatedName(name, partNumber)}`
        : `${name}`
    })
  );
  const orderItemPricesDetails = orderItemPrices?.map(item => item);
  const {
    companyName,
    isQuantityMergeEnabled,
    orderByName: orderBy,
    originalQuoteId,
    originalQuoteNumber
  } = orderExtendAttribute;

  const orderHeader = {
    ...orderInformation,
    ...orderExtendAttribute,
    savedCreditCard,
    requestByDate,
    purchaseOrder,
    orderBy,
    isDealerAcceptTermsAndCond,
    setDealerTermsAndCondHandle: () =>
      setDealerAcceptTermsAndCond(prev => !prev)
  };

  const paymentGatewayObj = {
    ...paymentGateway,
    getMidtransToken: checkoutActions.getMidtransToken,
    midtransPostback: checkoutActions.midtransPostback
  };

  let updatedOrderTotals = orderTotals?.formatted || orderTotals;
  const taxAndFreight = useSelector(s => s?.checkout?.taxAndFreight) || [];

  if (
    !isEmpty(taxAndFreight) &&
    getLocalStorage(RBD_UPDATE_FROM_SUMMARY_AND_PAYMENT) === 'y'
  ) {
    let taxObject = taxAndFreight?.[0];
    updatedOrderTotals = {
      ...orderTotals,
      ...taxObject?.orderTotals?.formatted,
      taxLabel: taxObject?.orderTotals?.taxLabels,
      unformattedTotaltax: taxObject?.orderTotals?.unformatted?.totaltax,
      unformattedShipping: taxObject?.orderTotals?.unformatted?.shipping,
      unformattedOrderAdjustment:
        taxObject?.orderTotals?.unformatted?.orderAdjustment,
      unformattedOrderTotal: taxObject?.orderTotals?.unformatted?.orderTotal,
      unformattedOrderSubTotal:
        taxObject?.orderTotals?.unformatted?.orderSubTotal
    };
  }
  const {
    orderSubTotal,
    orderTotal,
    shipping,
    totaltax,
    taxLabel,
    orderAdjustment,
    orderCatPrepaidCertificatesTotal,
    orderRemainingPaymentAmount
  } = updatedOrderTotals || {};

  const total =
    isCatCreditInternational && catCreditsGlobalAmountUsed
      ? `$${catCreditsGlobalAmountUsed}`
      : orderCatPrepaidCertificatesTotal;

  const subTotal =
    isCatCreditInternational && remainingCatCreditsGlobal
      ? `$${remainingCatCreditsGlobal}`
      : orderRemainingPaymentAmount;

  const pageLoading =
    (isSAndPLoading || isPreferencesDataLoading) && pendingPageLoading;

  const showPendingQuoteDetails = quoteAvailable && !hideQuoteDetails;

  const placingOrderUpdatesFeatureFlag = useSelector(
    state => state?.featureFlag?.[PCC_PS_PLACING_ORDER_UPDATES]
  );

  const pickUpAndDeliveryURL = `${PICKUP_AND_DELIVERY_PAGE}${location.search}`;

  const titleUpdatesFeatureFlag = useSelector(
    state => state?.featureFlag?.[PCC_PS_TITLE_UPDATES]
  );

  const psESpotHidingMobileViewFlag = useSelector(
    state => state.featureFlag[PCC_PS_HIDE_ESPOTS_MOBILE]
  );

  const orderPriceAvailabilityTotalsStatus = useSelector(
    s => s?.orders?.byId[orderId]?.orderPriceAvailabilityTotalsStatus
  );

  const isQuoteExpired = useSelector(
    s => s?.orders?.byId[orderId]?.isQuoteExpired
  );
  const userRoles = useSelector(s => s.common.userRoles);
  const orderStatus = useSelector(s => s.orders?.byId[orderId]?.orderStatus);
  const quoteType = useSelector(s => s.orders?.byId[orderId]?.orderType);

  const isLoading = orderPriceAvailabilityTotalsStatus === STATUS.PENDING;
  const successDisplay = orderPriceAvailabilityTotalsStatus === STATUS.RESOLVED;
  const isWarningDisplay =
    orderPriceAvailabilityTotalsStatus === STATUS.REJECTED;

  const displayPageLoader = () => {
    if (refreshPartsAvailabilityOnQuote && isPendingQuote) {
      const isPendingApproval = orderStatus === ORDER_STATUS.PENDING_APPROVAL;
      const isRejected = orderStatus === ORDER_STATUS.REJECTED;
      const isOrderTypeQot = quoteType === ORDER_TYPE_QOT;
      const isPendingApprovalQuote = isPendingApproval && isOrderTypeQot;
      const isRejectedQuote = isRejected && isOrderTypeQot;
      const isSubmitter = userRoles?.includes(USER_ROLES.SUBMITTER);
      const isAdmin = userRoles?.includes(USER_ROLES.ADMIN);
      const skipRefreshPartsAvailibility =
        isQuoteExpired ||
        isRejectedQuote ||
        (isPendingApprovalQuote && isSubmitter && !isAdmin);
      if (!skipRefreshPartsAvailibility && isLoading) {
        return (
          <div
            className={
              'd-flex justify-content-center align-items-center h-100 w-100 position-fixed top-0 start-0'
            }
          >
            <LoaderWithText
              containerStyles={styles['loading_container']}
              cellStyles={styles['loading_content']}
              size={WIDGET_LOADER_SIZE}
              title="WHILE_CHECKING_AVAIL"
              titleStyle="u-fs--14  line-height-16 "
            ></LoaderWithText>
          </div>
        );
      } else if (skipRefreshPartsAvailibility) {
        return pageLoading && <PageLoader />;
      }
    } else {
      return pageLoading && <PageLoader />;
    }
  };

  return (
    <div
      className={clx('checkout-summary-payment text-start', {
        container: !isPendingQuote && !isPendingOrder,
        'mt-4': !titleUpdatesFeatureFlag,
        'mt-3 mt-md-4': titleUpdatesFeatureFlag
      })}
    >
      <Conditional test={!isPendingQuote}>
        <CheckoutStepper cartURL={cartURL} />
      </Conditional>
      {isPendingOrder ? null : (
        <SummaryAndPaymentPageHeader
          isPendingQuote={isPendingQuote}
          isSAndPLoadSuccessful={isSAndPLoadSuccessful}
          orderId={orderId}
          orderHeader={orderHeader}
          selectedBillingMethod={selectedBillingMethod}
          selectedBillingAddress={selectedBillingAddress}
          quoteAvailable={quoteAvailable}
          originalQuoteId={originalQuoteId}
          originalQuoteNumber={originalQuoteNumber}
          hideQuoteDetails={hideQuoteDetails}
          isWarningDisplay={isWarningDisplay}
          successDisplay={successDisplay}
        />
      )}
      {displayPageLoader()}
      {(!isPendingQuote || showPendingQuoteDetails) &&
        isSAndPLoadSuccessful &&
        isPreferencesDataLoadCompleted && (
          <>
            <SummaryAndPaymentPage
              billingAddress={billingAddress}
              billingMethodOptions={billingMethodOptions}
              additionalBillingMethodOptions={additionalBillingMethodOptions}
              companyName={companyName}
              currencyNotation={currencyNotation}
              fulfillment={fulfillment}
              isPendingQuote={isPendingQuote}
              micoEnabled={micoEnabled}
              orderExtendAttribute={orderExtendAttribute}
              orderHeader={orderHeader}
              orderId={orderId}
              orderTotals={updatedOrderTotals}
              paymentTokenizationEnabled={paymentTokenizationEnabled}
              paymentGateway={paymentGatewayObj}
              punchoutOrderFailed={punchoutOrderFailed}
              quoteEnabled={quoteEnabled}
              saveAddress={checkoutActions.saveAddress}
              savePaymentDetails={checkoutActions.savePaymentDetails}
              selectedBillingMethod={selectedBillingMethod}
              selectedAlternativePayment={selectedAlternativePayment}
              selectedAdditionalBillingMethod={selectedAdditionalBillingMethod}
              selectedBillingAddress={selectedBillingAddress}
              setSelectedBillingMethod={
                checkoutActions.setSelectedBillingMethod
              }
              setSelectedAdditionalBillingMethod={
                checkoutActions.setSelectedAdditionalBillingMethod
              }
              setSelectedBillingAddress={
                checkoutActions.setSelectedBillingAddress
              }
              setSelectedAlternativePayment={
                checkoutActions.setSelectedAlternativePayment
              }
              updateOrderedBy={ordersActions.updateOrderedBy}
              promoCode={promoCode}
              applyPromoCode={checkoutActions.applyPromoCode}
              removePromoCode={checkoutActions.removePromoCode}
              placeOrder={checkoutActions.placeOrder}
              savePoNumber={checkoutActions.savePoNumber}
              skipCreditCard={skipCreditCard}
              vatTaxEnabled={vatTaxEnabled}
              isSAndPLoading={isSAndPLoading}
            />
            <ItemsInOrder
              isPendingQuote={isPendingQuote}
              currencyNotation={currencyNotation}
              includeGST={includeGST}
              items={orderedItems}
              showGST={showGST}
              vatTaxEnabled={vatTaxEnabled}
              isQuantityMergeEnabled={isQuantityMergeEnabled}
              showOptimizationChanges
              titleUpdatesActive={titleUpdatesFeatureFlag}
              isWarningDisplay={isWarningDisplay}
              successDisplay={successDisplay}
              orderItemPricesDetails={orderItemPricesDetails}
            />
            <div
              className={clx(
                'row justify-content-end',
                styles['px-print-order-total']
              )}
            >
              <div
                className={clx(
                  'col-12 col-md-6',
                  styles['summary-payment__order-total']
                )}
              >
                <SummaryOrderTotals
                  currency={currencyNotation}
                  catCreditTotal={total}
                  orderRemainingPaymentAmount={subTotal}
                  isPendingQuote={isPendingQuote}
                  orderAdjustment={orderAdjustment}
                  subtotal={orderSubTotal}
                  shippingCost={shipping}
                  tax={orderTotals?.formatted?.totalTax || totaltax}
                  taxLabel={taxLabel}
                  total={orderTotal}
                  vatTaxEnabled={vatTaxEnabled}
                  zeroFreightMessageEnabled={zeroFreightMessageEnabled}
                />
              </div>
            </div>
            {placingOrderUpdatesFeatureFlag && !isMD && (
              <a
                className={clx(
                  'lh-1 text-univers btn btn-lg btn-link fw-bold d-flex justify-content-center',
                  styles['summary-payment__backLink']
                )}
                href={pickUpAndDeliveryURL}
              >
                {t('BACK')}
              </a>
            )}
            {isPendingQuote ? (
              <div id="pending-quote-details-footer-action"></div>
            ) : null}
            {!isQuantityMergeEnabled && (
              <div
                className={clx({
                  'pt-4': !titleUpdatesFeatureFlag,
                  'pt-3 pt-md-4': titleUpdatesFeatureFlag
                })}
              >
                <div className={styles['summary-payment__requiredLabel']}>
                  <span
                    className={clx(
                      styles['summary-payment__requiredLabel'],
                      ' d-sm-inline-block'
                    )}
                  >
                    &dagger;&nbsp;
                  </span>
                  {t('UN_MERGE_AVAILABILITY')}
                </div>
              </div>
            )}
          </>
        )}
    </div>
  );
};

export default Container;

Container.propTypes = containerPropTypes;
